import { SlotVisualKind } from "./scheduleGridTypes";
import type { MobileTimelineRowKind } from "./mobileConsultantAgendaModel";

export type SlotTheme = {
  shell: string;
  label: string;
  badge: string;
};

export type MobileScheduleTheme = {
  shell: string;
  badge: string;
};

/**
 * Shared status colors. Desktop and mobile keep independent layouts while
 * reusing the same semantic palette for schedule states.
 */
export const SCHEDULE_STATUS_STYLE_TOKENS = {
  scheduled: {
    border: "border-[#38A169]",
    softBackground: "bg-[#C6F6D5]",
    label: "text-[#14532D]",
    badge: "bg-[#C6F6D5] text-[#166534]",
  },
  video: {
    border: "border-[#3182CE]",
    softBackground: "bg-[#BEE3F8]",
    label: "text-[#1A365D]",
    badge: "bg-[#BEE3F8] text-[#2C5288]",
  },
  mixed: {
    border: "border-[#319795]",
    softBackground: "bg-[#B2F5EA]",
    label: "text-[#134E4A]",
    badge: "bg-[#B2F5EA] text-[#234E52]",
  },
  completed: {
    border: "border-[#276749]",
    softBorder: "border-[#C6F6D5]",
    background: "bg-[#276749]",
    label: "text-white",
    badge: "bg-[#276749] text-white",
  },
  noShow: {
    border: "border-[#DD6B20]",
    softBackground: "bg-[#FFFAF0]",
    desktopBackground: "bg-[#FBD38D]",
    label: "text-[#652B19]",
    badge: "bg-[#FBD38D] text-[#9C4221]",
  },
  pending: {
    border: "border-[#D69E2E]",
    softBackground: "bg-[#FFFAF0]",
    badge: "bg-[#FEEBC8] text-[#744210]",
  },
  blocked: {
    border: "border-[#A0AEC0]",
    background: "bg-[#EDF2F7]",
    badge: "bg-[#E2E8F0] text-[#4A5568]",
  },
  free: {
    border: "border-[#A0AEC0]",
    badge: "border-[#CBD5E0] text-[#718096]",
  },
} as const;

export const SCHEDULE_GRID_CONSULTANT_MIN_PX = 190;
export const SCHEDULE_GRID_CONSULTANT_MAX_PX = 205;
export const SCHEDULE_GRID_SLOT_MIN_PX = 100;
export const SCHEDULE_GRID_SLOT_FIXED_MIN_PX = 105;
export const SCHEDULE_GRID_SLOT_FIXED_MAX_PX = 115;
export const SCHEDULE_GRID_CELL_HEIGHT_PX = 62;
export const SCHEDULE_GRID_GAP_PX = 5;

export type ScheduleGridColumnOptions = {
  /** When true, hour columns keep a fixed min width (sidebar open). */
  preserveSlotWidth?: boolean;
};

export function buildScheduleGridTemplateColumns(
  hourCount: number,
  options?: ScheduleGridColumnOptions
): string {
  const consultant = `minmax(${SCHEDULE_GRID_CONSULTANT_MIN_PX}px, ${SCHEDULE_GRID_CONSULTANT_MAX_PX}px)`;
  const hourColumns = options?.preserveSlotWidth
    ? `repeat(${hourCount}, minmax(${SCHEDULE_GRID_SLOT_FIXED_MIN_PX}px, ${SCHEDULE_GRID_SLOT_FIXED_MAX_PX}px))`
    : `repeat(${hourCount}, minmax(${SCHEDULE_GRID_SLOT_MIN_PX}px, 1fr))`;

  return `${consultant} ${hourColumns}`;
}

export function buildScheduleGridMinWidthPx(
  hourCount: number,
  options?: ScheduleGridColumnOptions
): number {
  const slotWidth = options?.preserveSlotWidth
    ? SCHEDULE_GRID_SLOT_FIXED_MIN_PX
    : SCHEDULE_GRID_SLOT_MIN_PX;
  const gaps = Math.max(0, hourCount) * SCHEDULE_GRID_GAP_PX;

  return (
    SCHEDULE_GRID_CONSULTANT_MAX_PX +
    hourCount * slotWidth +
    gaps +
    8
  );
}

export const SLOT_THEME: Record<SlotVisualKind, SlotTheme> = {
  empty: {
    shell: "border border-[#CBD5E0] bg-[#F7FAFC]",
    label: "text-transparent",
    badge: "",
  },
  free: {
    shell:
      `border border-dashed ${SCHEDULE_STATUS_STYLE_TOKENS.free.border} bg-white hover:border-[#c4552b] hover:bg-[#FFF5F0]`,
    label: "text-[#4A5568]",
    badge: "",
  },
  blocked: {
    shell: `border ${SCHEDULE_STATUS_STYLE_TOKENS.blocked.border} ${SCHEDULE_STATUS_STYLE_TOKENS.blocked.background}`,
    label: "text-[#2D3748]",
    badge: "text-[#4A5568]",
  },
  scheduled: {
    shell: `border ${SCHEDULE_STATUS_STYLE_TOKENS.scheduled.border} ${SCHEDULE_STATUS_STYLE_TOKENS.scheduled.softBackground}`,
    label: SCHEDULE_STATUS_STYLE_TOKENS.scheduled.label,
    badge: "bg-white text-[#166534] ring-1 ring-[#38A169]/30",
  },
  video: {
    shell: `border ${SCHEDULE_STATUS_STYLE_TOKENS.video.border} ${SCHEDULE_STATUS_STYLE_TOKENS.video.softBackground}`,
    label: SCHEDULE_STATUS_STYLE_TOKENS.video.label,
    badge: "bg-white text-[#2C5288] ring-1 ring-[#3182CE]/30",
  },
  mixed: {
    shell: `border ${SCHEDULE_STATUS_STYLE_TOKENS.mixed.border} ${SCHEDULE_STATUS_STYLE_TOKENS.mixed.softBackground}`,
    label: SCHEDULE_STATUS_STYLE_TOKENS.mixed.label,
    badge: "bg-white text-[#234E52] ring-1 ring-[#319795]/30",
  },
  completed: {
    shell: `border ${SCHEDULE_STATUS_STYLE_TOKENS.completed.border} ${SCHEDULE_STATUS_STYLE_TOKENS.completed.background} text-white`,
    label: SCHEDULE_STATUS_STYLE_TOKENS.completed.label,
    badge: "bg-white/25 text-white ring-1 ring-white/30",
  },
  no_show: {
    shell: `border ${SCHEDULE_STATUS_STYLE_TOKENS.noShow.border} ${SCHEDULE_STATUS_STYLE_TOKENS.noShow.desktopBackground}`,
    label: SCHEDULE_STATUS_STYLE_TOKENS.noShow.label,
    badge: "bg-white text-[#9C4221] ring-1 ring-[#DD6B20]/35",
  },
};

export const MOBILE_SCHEDULE_THEME: Record<
  MobileTimelineRowKind,
  MobileScheduleTheme
> = {
  // Filled cards (see mobileScheduleCardStyle) use a white pill so the badge stands out.
  scheduled: {
    shell: `${SCHEDULE_STATUS_STYLE_TOKENS.scheduled.border} bg-white`,
    badge: "bg-white text-[#166534] ring-1 ring-[#2F855A]/40",
  },
  video: {
    shell: `${SCHEDULE_STATUS_STYLE_TOKENS.video.border} bg-white`,
    badge: "bg-white text-[#2C5288] ring-1 ring-[#3182CE]/40",
  },
  mixed: {
    shell: `${SCHEDULE_STATUS_STYLE_TOKENS.mixed.border} bg-white`,
    badge: "bg-white text-[#234E52] ring-1 ring-[#319795]/40",
  },
  // Same as the desktop grid: completed is the dark green, scheduled the light one.
  completed: {
    shell: `${SCHEDULE_STATUS_STYLE_TOKENS.completed.border} ${SCHEDULE_STATUS_STYLE_TOKENS.completed.background}`,
    badge: "bg-white/25 text-white ring-1 ring-white/30",
  },
  no_show: {
    shell: `${SCHEDULE_STATUS_STYLE_TOKENS.noShow.border} ${SCHEDULE_STATUS_STYLE_TOKENS.noShow.softBackground}`,
    badge: SCHEDULE_STATUS_STYLE_TOKENS.noShow.badge,
  },
  post_visit_pending: {
    shell: `${SCHEDULE_STATUS_STYLE_TOKENS.pending.border} ${SCHEDULE_STATUS_STYLE_TOKENS.pending.softBackground}`,
    badge: SCHEDULE_STATUS_STYLE_TOKENS.pending.badge,
  },
  blocked: {
    shell: `${SCHEDULE_STATUS_STYLE_TOKENS.blocked.border} ${SCHEDULE_STATUS_STYLE_TOKENS.blocked.background}`,
    badge: SCHEDULE_STATUS_STYLE_TOKENS.blocked.badge,
  },
  free: {
    shell: `border-dashed ${SCHEDULE_STATUS_STYLE_TOKENS.free.border} bg-white`,
    badge: `border border-dashed ${SCHEDULE_STATUS_STYLE_TOKENS.free.badge}`,
  },
};

export const LEGEND_SWATCH: Record<string, string> = {
  scheduled: "border border-[#38A169] bg-[#C6F6D5]",
  video: "border border-[#3182CE] bg-[#BEE3F8]",
  completed: "border border-[#276749] bg-[#276749]",
  noshow: "border border-[#DD6B20] bg-[#FBD38D]",
  blocked: "border border-[#A0AEC0] bg-[#EDF2F7]",
  free: "border border-dashed border-[#A0AEC0] bg-white",
};
